// -----------------------------------------------------------------
// <copyright
//   file="Izenda.ConnectionList.js"
//   company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// -----------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { INPUT, notifyChange } from 'EventUtils';
import { clone, getOr, noop } from 'lodash/fp';

import uiState from 'logic/Izenda.UiState';
import compare from 'share/utils/Izenda.PropsComparison';
import connect from 'share/enhancers/Izenda.Connect';
import localizer from 'share/localization/Izenda.Localizer';

import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import SearchTextBox from 'share/components/controls/Izenda.SearchTextBox';
import List from 'share/components/controls/Izenda.List';

import ConnectionItem from './Izenda.ConnectionItem';
import ConnectionMasterPopup from "../ConnectionMaster/Izenda.ConnectionMasterPopup";
import ConnectionStringBuilder
  from 'share/components/controls/connectionStringBuilder/Izenda.ConnectionStringBuilder';
import ConnectionDetail from 'logic/models/Izenda.ConnectionDetail';
import ConnectionStringBuilderRest
  from 'share/components/controls/ConnectionStringBuilderRest/Izenda.ConnectionStringBuilderRest';
import ConnectionDetailStore from 'logic/Izenda.ConnectionDetailStore';
import ConnectionStore from 'logic/Izenda.ConnectionStore';
import ConnectionUtils from "../Izenda.ConnectionUtils";

/**
 * Connection List component
 */
@connect()
export default class ConnectionList extends Component {
  static propTypes = {
    isMiddlePanelMinimum: PropTypes.bool,
    store: PropTypes.object.isRequired
  };

  static defaultProps = {
    isMiddlePanelMinimum: false
  };

  componentDidMount() {
    const { loader } = this.props;
    const store: ConnectionStore = this.props.store;

    loader.startLoading();
    store
      .loadList()
      .then(() => {
        if (store.list && store.list.length > 0)
          store.setConnectionDetail(store.list[0]);
        return null;
      })
      .finally(loader.endLoading.bind(loader));
  }

  shouldComponentUpdate(nextProps) {
    return !compare(nextProps, this.props);
  }

  /**
   * When there is only one item in searched connection list, user can press enter
   * to select it.
   * @param  {Object} event React-event
   */
  selectSearchedConnection = (event) => {
    const { store } = this.props;
    if (event.key === 'Enter' && store.filteredConnections.length === 1) {
      store.item = store.filteredConnections[0];
    }
  };

  /**
   * Put connection to list to complete
   * @param {ConnectionDetail} connetionDetail connection which was configured.
   */
  onConnectionWasAdded = (connetionDetail) => {
    const { store } = this.props;
    connetionDetail.resetModelError();
    store.setConnectionDetail(connetionDetail);
    store.addConnectionToList(connetionDetail);
  };

  /**
   * Show choose DB type popup. This functions doesn't touch current connection store
   * and does everything in store passed through the argument.
   * @param {ConnectionStore} connectionStore "store.item" object is used as current connection details in this modal.
   * @returns {Promise}
   */
  openChooseDbTypePopup = (connectionStore) => {
    const { props } = this;

    // Open popup and wait while user will select dbType.
    // DbType will be set in "connectionStore.item.serverTypeId"
    return new Promise((resolve, reject) => {
      props.openPopup(ConnectionMasterPopup, {
        connectionStore: connectionStore
      }).then(
        // Resolve
        result => {
          if (!result) {
            reject('Empty result');
            return;
          }
          resolve();
        },
        // Reject
        err => reject(err));
    });
  };

  /**
   * Open excel file upload popup
   * @param {ConnectionDetailStore} connectionDetailStore
   */
  openExcelFileUploadPopup(connectionDetailStore) {
    const { props } = this;
    return ConnectionUtils.openUploadExcelPopup(props, connectionDetailStore);
  }

  /**
   * Open connection string popup
   * @param {object} dbType db type object
   * @param {ConnectionStore} connectionStore connection model
   */
  openConnectionStringPopup = (dbType, connectionStore) => {
    const { props } = this;
    const store: ConnectionStore = props.store;

    const connectionDetailStore: ConnectionDetailStore = new ConnectionDetailStore(connectionStore);
    const connectionDetail: ConnectionDetail = connectionStore.item;
    connectionDetail.setServerTypeId(dbType.id);
    connectionDetail.serverTypeName = dbType.shortName;

    const dbTypeShortName = getOr('', 'shortName', dbType);
    if (dbTypeShortName.toUpperCase() === 'EXCEL') {

      // Excel connection string builder
      this
        .openExcelFileUploadPopup(connectionDetailStore)
        .then(
          connectionString => {
            connectionDetail.setConnectionString(connectionString);
            // Put into MAIN props.store delete queue, because current connectionStore is temporary and will be
            // disposed after all dialogs will be closed
            store.addTempExcelFileToDeleteQueue(connectionString);
            connectionDetailStore
              .performConnection(connectionString)
              .then(result => {
                if (result.success === false) {
                  props.confirmMessage('error', getOr(localizer.getText('COMMON_MESSAGES_GENERIC_ERROR'), 'messages[0].messages[0]', result));
                } else {
                  this.onConnectionWasAdded(connectionDetail);
                }
              });
          },
          errorText => {
            props.confirmMessage('error', errorText);
          });

    } else if (dbTypeShortName.toUpperCase() === 'REST') {

      // For REST we show connection string builder
      props
        .openPopup(ConnectionStringBuilderRest, {
          connectionDetailStore: connectionDetailStore,
          endpointModels: []
        })
        .then(({ connectionString, endpointModels }) => {
          connectionDetail.endpointModels = endpointModels;
          this.onConnectionWasAdded(connectionDetail)
        });

    } else {

      // Default connection string builder
      props
        .openPopup(ConnectionStringBuilder, {
          isConnectionStringEnabled: true,
          serverType: dbType.name,
          connectionDetailStore: connectionDetailStore,
          onOk: () => {
            // We don't need to call performConnection as in previous cases because it was made
            // inside of popup before calling exit handler (to show errors before exit)
            this.onConnectionWasAdded(connectionDetail);
            return true;
          }
        });
    }
  };

  /**
   * Add a new connection. New connection detail is shown.
   */
  addNewConnection = () => {
    let store: ConnectionStore = this.props.store;

    // Ask dirty confirmation before start
    this.props.confirmDirtyForm(uiState.isDirty, {
      onConfirmDirtyForm: uiState.onOkDirtyForm
    }).then(() => {

      store.filter = '';
      store.deleteTempExcelFiles();

      // Create new connection detail in new isolated connection store.
      const newConnectionStore = clone(store);
      newConnectionStore.addConnection().then(noop);

      // First step is choosing db type
      this
        .openChooseDbTypePopup(newConnectionStore)
        .then(
          () => {
            // Second step is to open connection string builder popup
            const dbTypeId = newConnectionStore.item.serverTypeId;
            const dbType = newConnectionStore.findDbTypeInList(dbTypeId);
            this.openConnectionStringPopup(dbType, newConnectionStore)
          },
          noop // do nothing if cancelled
        );
    });
  };

  renderHeaderContent() {
    const { store } = this.props;
    return (
      <div className="izenda-MiddlePanel-search">
        <a
          className="u_block overflow-text"
          onClick={this.addNewConnection}
          title={localizer.getText("CONNECTION_LABEL_ADD_CONNECTION")}>
          <span className="icon-plus"/>
          {localizer.getText("CONNECTION_LABEL_ADD_CONNECTION")}
        </a>
        <div className="search-box">
          <SearchTextBox
            value={store.filter}
            onChange={notifyChange(store.setFilter.bind(store), INPUT)}
            onKeyPress={this.selectSearchedConnection}/>
        </div>
        <hr/>
      </div>
    );
  }

  renderListConnection() {
    const { isMiddlePanelMinimum, store } = this.props;

    return (
      <div className="izenda-ConnectionList-content izenda-MiddlePanel-content">
        <ScrollBox>
          <List itemClass="connection-string-item">
            {store.filteredConnections.map((item, idx) => {
              return (
                <ConnectionItem
                  key={idx}
                  dataSource={item}
                  store={store}
                  isMiddlePanelMinimum={isMiddlePanelMinimum}>
                </ConnectionItem>
              );
            })}
          </List>
        </ScrollBox>
      </div>
    );
  }

  render() {
    return (
      <div className="izenda-ConnectionList u_fullHeight u-posRelative u-bgWhite izenda-Middle-section">
        {this.renderHeaderContent()}
        {this.renderListConnection()}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/Connection/ConnectionList/Izenda.ConnectionList.js